import React, { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import { HelpCircle, X, BookOpen, ExternalLink } from "lucide-react";
import { Button } from "./ui/button";
import { API, useAuth } from "../App";

const HIDDEN_ROUTES = ["/login", "/pending", "/help", "/cp/", "/tools/", "/partner-form"];

const ContextualHelpBubble = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(false);
  const fetchedForPath = useRef(null);

  const hidden = HIDDEN_ROUTES.some(r => location.pathname === r || location.pathname.startsWith(r));

  useEffect(() => {
    if (!open || hidden || !user) return;
    if (fetchedForPath.current === location.pathname) return;
    fetchedForPath.current = location.pathname;
    setLoading(true);
    axios
      .get(`${API}/help/contextual?page=${encodeURIComponent(location.pathname)}`)
      .then(res => setArticles(res.data.articles || []))
      .catch(() => setArticles([]))
      .finally(() => setLoading(false));
  }, [open, location.pathname, hidden, user]);

  if (hidden || !user) return null;

  const openArticle = (articleId) => {
    setOpen(false);
    navigate(`/help?article=${articleId}`);
  };

  return (
    <>
      {!open && (
        <button
          type="button"
          aria-label="Open contextual help"
          onClick={() => setOpen(true)}
          data-testid="contextual-help-bubble"
          className="fixed bottom-6 right-6 z-40 h-12 w-12 rounded-full bg-slate-900 text-white shadow-lg hover:scale-105 hover:bg-slate-800 transition-transform flex items-center justify-center"
        >
          <HelpCircle className="h-6 w-6" />
        </button>
      )}

      {open && (
        <div
          className="fixed bottom-6 right-6 z-50 w-80 max-w-[90vw] rounded-xl border border-slate-200 bg-white shadow-xl overflow-hidden"
          data-testid="contextual-help-panel"
        >
          <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100 bg-slate-50">
            <div className="flex items-center gap-2">
              <BookOpen className="w-4 h-4 text-slate-700" />
              <span className="text-sm font-semibold text-slate-800 font-['Manrope']">Help for this page</span>
            </div>
            <button
              type="button"
              aria-label="Close help"
              onClick={() => setOpen(false)}
              className="text-slate-500 hover:text-slate-800"
              data-testid="contextual-help-close"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
          <div className="max-h-80 overflow-y-auto">
            {loading ? (
              <div className="p-4 text-sm text-slate-400">Loading tips…</div>
            ) : articles.length === 0 ? (
              <div className="p-4 space-y-2">
                <p className="text-sm text-slate-500">
                  No page-specific articles yet. Browse the full Help Center for guides across every feature.
                </p>
              </div>
            ) : (
              <ul className="divide-y divide-slate-100">
                {articles.map(a => (
                  <li key={a.article_id}>
                    <button
                      type="button"
                      onClick={() => openArticle(a.article_id)}
                      className="w-full text-left px-4 py-3 hover:bg-slate-50"
                      data-testid={`contextual-help-article-${a.article_id}`}
                    >
                      <div className="text-xs text-slate-400 mb-0.5">{a.category}</div>
                      <div className="text-sm font-medium text-slate-800">{a.title}</div>
                      {a.summary && (
                        <div className="text-xs text-slate-500 line-clamp-2 mt-0.5">{a.summary}</div>
                      )}
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
          <div className="px-4 py-3 border-t border-slate-100 bg-slate-50">
            <Button
              size="sm"
              variant="outline"
              className="w-full"
              onClick={() => {
                setOpen(false);
                navigate("/help");
              }}
              data-testid="contextual-help-browse-all"
            >
              Browse all articles <ExternalLink className="w-3.5 h-3.5 ml-1" />
            </Button>
          </div>
        </div>
      )}
    </>
  );
};

export default ContextualHelpBubble;
